import React, { useEffect, useState } from 'react';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import leaveApi from './leaveApi';

const LeaveRequestCard = ({
  request,
  type = 'applied', // 'applied', 'history', 'team'
  onApprove,
  onReject,
  onViewDetails,
  canApprove = false,
}) => {
  const [raisedByName, setRaisedByName] = useState(
    request.raised_by || 'Unknown User'
  );
  const [decisionByName, setDecisionByName] = useState(
    request.decision_by || 'Unknown User'
  );
  const [isLoadingNames, setIsLoadingNames] = useState(false);

  const statusInfo = leaveApi.getStatusInfo(request.status);
  const formattedDates = request.date_list
    ? request.date_list.map(date =>
        leaveApi.formatDateForDisplay(new Date(date))
      )
    : [];

  useEffect(() => {
    const fetchUserNames = async () => {
      if (!request.raised_by && !request.decision_by) return;

      setIsLoadingNames(true);
      try {
        const promises = [];

        if (request.raised_by) {
          promises.push(
            leaveApi.getUserDisplayName(request.raised_by).then(name => {
              console.log(
                `Fetched name for raised_by ${request.raised_by}:`,
                name
              );
              setRaisedByName(name);
            })
          );
        }

        if (request.decision_by) {
          promises.push(
            leaveApi.getUserDisplayName(request.decision_by).then(name => {
              console.log(
                `Fetched name for decision_by ${request.decision_by}:`,
                name
              );
              setDecisionByName(name);
            })
          );
        }

        await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching user names:', error);
        // Fallback to showing user IDs
        if (request.raised_by) setRaisedByName(request.raised_by);
        if (request.decision_by) setDecisionByName(request.decision_by);
      } finally {
        setIsLoadingNames(false);
      }
    };

    fetchUserNames();
  }, [request.raised_by, request.decision_by]);

  const formatRequestTime = request => {
    if (request.date_list?.length) {
      const sortedDates = [...request.date_list].sort();
      const firstDate = leaveApi.formatDateForDisplay(new Date(sortedDates[0]));

      if (sortedDates.length === 1) {
        return firstDate;
      }

      const lastDate = leaveApi.formatDateForDisplay(
        new Date(sortedDates[sortedDates.length - 1])
      );
      return `${firstDate} - ${lastDate}`;
    }

    if (request.raised_at) {
      return leaveApi.formatDateForDisplay(new Date(request.raised_at));
    }

    return 'Date unavailable';
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderAppliedCard = () => (
    <Card.Glassmorphism className="p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              request.status
            )}`}
          >
            {statusInfo.icon} {statusInfo.label}
          </span>
          <span className="text-sm text-gray-500">
            {request.number_of_days} day{request.number_of_days > 1 ? 's' : ''}
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {formatRequestTime(request)}
        </span>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          {request.leave_type}
        </h6>
        <p className={typography.p} style={{ color: '#666666' }}>
          {request.reason || 'No reason provided'}
        </p>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          Requested Dates:
        </h6>
        <div className="flex flex-wrap gap-1">
          {formattedDates.map((date, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
            >
              {date}
            </span>
          ))}
        </div>
      </div>

      {request.remarks && (
        <div className="mb-3">
          <h6
            className={typography.h6}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Remarks:
          </h6>
          <p className={typography.p} style={{ color: '#666666' }}>
            {request.remarks}
          </p>
        </div>
      )}

      {request.decision_by && (
        <div className="text-sm text-gray-500">
          Decided by: {isLoadingNames ? 'Loading...' : decisionByName}
        </div>
      )}
    </Card.Glassmorphism>
  );

  const renderHistoryCard = () => (
    <Card.Glassmorphism className="p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              request.status
            )}`}
          >
            {statusInfo.icon} {statusInfo.label}
          </span>
          <span className="text-sm text-gray-500">
            {request.number_of_days} day{request.number_of_days > 1 ? 's' : ''}
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {formatRequestTime(request)}
        </span>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          {request.leave_type}
        </h6>
        <p className={typography.p} style={{ color: '#666666' }}>
          {request.reason || 'No reason provided'}
        </p>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          Requested Dates:
        </h6>
        <div className="flex flex-wrap gap-1">
          {formattedDates.map((date, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
            >
              {date}
            </span>
          ))}
        </div>
      </div>

      {request.remarks && (
        <div className="mb-3">
          <h6
            className={typography.h6}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Remarks:
          </h6>
          <p className={typography.p} style={{ color: '#666666' }}>
            {request.remarks}
          </p>
        </div>
      )}

      <div className="text-sm text-gray-500">
        {request.decision_by &&
          `Decided by: ${isLoadingNames ? 'Loading...' : decisionByName}`}
        {request.raised_by &&
          ` • Requested by: ${isLoadingNames ? 'Loading...' : raisedByName}`}
      </div>
    </Card.Glassmorphism>
  );

  const renderTeamCard = () => (
    <Card.Glassmorphism
      className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onViewDetails}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            ⏳ Pending
          </span>
          <span className="text-sm text-gray-500">
            {request.number_of_days} day{request.number_of_days > 1 ? 's' : ''}
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {formatRequestTime(request)}
        </span>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          {request.leave_type}
        </h6>
        <p className={typography.p} style={{ color: '#666666' }}>
          {request.reason || 'No reason provided'}
        </p>
      </div>

      <div className="mb-3">
        <h6
          className={typography.h6}
          style={{ color: '#000000', marginBottom: '0.5rem' }}
        >
          Requested Dates:
        </h6>
        <div className="flex flex-wrap gap-1">
          {formattedDates.map((date, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
            >
              {date}
            </span>
          ))}
        </div>
      </div>

      <div className="text-sm text-gray-500">
        Requested by: {isLoadingNames ? 'Loading...' : raisedByName}
      </div>

      {canApprove && (
        <div className="mt-3 flex gap-2">
          <button
            onClick={e => {
              e.stopPropagation();
              onApprove && onApprove(request);
            }}
            className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={e => {
              e.stopPropagation();
              onReject && onReject(request);
            }}
            className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
          >
            Reject
          </button>
        </div>
      )}
    </Card.Glassmorphism>
  );

  switch (type) {
    case 'applied':
      return renderAppliedCard();
    case 'history':
      return renderHistoryCard();
    case 'team':
      return renderTeamCard();
    default:
      return renderAppliedCard();
  }
};

export default LeaveRequestCard;
